import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, Check, X } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/auth";

const COLORS = ["#1F5BFF", "#17B6E0", "#3AA64B", "#F5A524", "#E84393", "#7C3AED"];

const EMPTY = {
  faqs: { q: "", a: "", order: 99, active: true },
  testimonials: { quote: "", name: "", role: "", color: COLORS[0], order: 99, active: true },
};

const inputCls = "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";

function ItemForm({ kind, initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="rounded-2xl border-2 border-iqblue/30 bg-white p-5 space-y-3" data-testid={`cms-form-${kind}`}>
      {kind === "faqs" ? (
        <>
          <input value={form.q} onChange={set("q")} placeholder="Question" required className={inputCls} data-testid="cms-f-q" />
          <textarea value={form.a} onChange={set("a")} placeholder="Answer" required rows={3} className={inputCls} data-testid="cms-f-a" />
        </>
      ) : (
        <>
          <textarea value={form.quote} onChange={set("quote")} placeholder="Quote" required rows={3} className={inputCls} data-testid="cms-t-quote" />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.name} onChange={set("name")} placeholder="Name" required className={inputCls} data-testid="cms-t-name" />
            <input value={form.role} onChange={set("role")} placeholder="Role, City" required className={inputCls} data-testid="cms-t-role" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ink/50">Color</span>
            {COLORS.map((c) => (
              <button type="button" key={c} onClick={() => setForm({ ...form, color: c })} data-testid={`cms-color-${c.slice(1)}`} className={`h-7 w-7 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-ink scale-110" : ""}`} style={{ background: c }} aria-label={c} />
            ))}
          </div>
        </>
      )}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-xs font-semibold text-ink/60">
          <input type="number" value={form.order} onChange={set("order")} className="w-16 rounded-lg border border-ink/15 px-2 py-1.5 text-xs" data-testid="cms-order" /> order
        </label>
        <label className="flex items-center gap-2 text-xs font-semibold text-ink/60">
          <input type="checkbox" checked={form.active} onChange={set("active")} data-testid="cms-active" /> visible on site
        </label>
      </div>
      <div className="flex gap-2.5">
        <button type="submit" disabled={busy} data-testid="cms-save-button" className="flex items-center gap-1.5 bg-ink text-paper text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
        </button>
        <button type="button" onClick={onCancel} data-testid="cms-cancel-button" className="flex items-center gap-1.5 border border-ink/15 text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-ink hover:text-paper transition-colors">
          <X className="h-3.5 w-3.5" /> Cancel
        </button>
      </div>
    </form>
  );
}

export default function CmsPanel() {
  const [kind, setKind] = useState("faqs");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (k = kind) => {
    const res = await authFetch(`/api/admin/cms/${k}`);
    if (res.ok) setItems(await res.json());
  };

  useEffect(() => {
    setEditing(null);
    setItems(null);
    load(kind);
  }, [kind]);

  const save = async (form) => {
    setBusy(true);
    const isNew = editing === "new";
    const res = await authFetch(isNew ? `/api/admin/cms/${kind}` : `/api/admin/cms/${kind}/${editing}`, {
      method: isNew ? "POST" : "PUT",
      body: JSON.stringify({ data: { ...form, order: Number(form.order) } }),
    });
    if (!res.ok) toast.error("Save failed");
    else {
      toast.success("Saved — live on the site now");
      setEditing(null);
      await load();
    }
    setBusy(false);
  };

  const toggleActive = async (item) => {
    await authFetch(`/api/admin/cms/${kind}/${item.id}`, { method: "PUT", body: JSON.stringify({ data: { active: !item.active } }) });
    await load();
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this item permanently?")) return;
    await authFetch(`/api/admin/cms/${kind}/${item.id}`, { method: "DELETE" });
    toast.success("Deleted");
    await load();
  };

  return (
    <div data-testid="cms-panel">
      <div className="inline-flex rounded-full border border-ink/15 p-1" data-testid="cms-tabs">
        {[["faqs", "FAQs"], ["testimonials", "Testimonials"]].map(([k, label]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`cms-tab-${k}`} className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-300 ${kind === k ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"}`}>
            {label}
          </button>
        ))}
      </div>

      {!items && <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-iqblue" /></div>}

      {items && (
        <div className="mt-6 space-y-4">
          {items.map((item) => (
            <motion.div key={item.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`rounded-2xl bg-cream border border-ink/10 p-5 ${!item.active ? "opacity-55" : ""}`} data-testid={`cms-item-${item.id}`}>
              {editing === item.id ? (
                <ItemForm kind={kind} initial={{ ...item }} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-sm tracking-tight truncate">
                      {kind === "faqs" ? item.q : `"${(item.quote || "").slice(0, 80)}${(item.quote || "").length > 80 ? "…" : ""}"`}
                    </div>
                    <div className="text-xs text-ink/50 mt-1">
                      {kind === "faqs" ? (item.a || "").slice(0, 110) + ((item.a || "").length > 110 ? "…" : "") : `${item.name} · ${item.role}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => toggleActive(item)} data-testid={`cms-toggle-${item.id}`} className="h-8 w-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-ink hover:text-paper transition-colors" title={item.active ? "Hide" : "Show"}>
                      {item.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={() => setEditing(item.id)} data-testid={`cms-edit-${item.id}`} className="h-8 w-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-iqblue hover:text-paper hover:border-iqblue transition-colors">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(item)} data-testid={`cms-delete-${item.id}`} className="h-8 w-8 rounded-full border border-ink/15 flex items-center justify-center hover:bg-iqpink hover:text-paper hover:border-iqpink transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}

          {editing === "new" ? (
            <ItemForm kind={kind} initial={{ ...EMPTY[kind] }} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
          ) : (
            <button onClick={() => setEditing("new")} data-testid="cms-add-button" className="w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ink/20 py-5 text-sm font-semibold text-ink/55 hover:border-iqblue hover:text-iqblue transition-colors">
              <Plus className="h-4 w-4" /> Add {kind === "faqs" ? "a question" : "a testimonial"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
